<script setup lang="ts">
// A photo or video in a grid (HRTH-164.6): its thumbnail, a play badge on videos. Tap opens
// it; long-press (phones) or right-click, or the ⋯ in its corner, opens its actions.
import { entryTitle, fileName } from '../lib/libraryEntries'
import type { OpenEntry } from '../lib/libraryPlay'
import { formatDuration } from '../lib/pattern'
import EntryImage from './EntryImage.vue'
import Icon from './Icon.vue'

const props = defineProps<{ file: OpenEntry; selecting?: boolean; selected?: boolean }>()
const emit = defineEmits<{ open: []; more: [] }>()
const complete = () => props.file.entry.blobs.some((b) => b.role === 'original')
</script>

<template>
  <div
    class="group relative aspect-square overflow-hidden"
    :class="selected ? 'outline-2 -outline-offset-2 outline-ink' : ''"
    :data-testid="`library-${file.meta.kind === 'video' ? 'video' : 'photo'}-${fileName(file.meta.path)}`"
  >
    <button
      class="field-grid flex h-full w-full items-center justify-center text-text-muted"
      :aria-label="entryTitle(file.meta)"
      data-testid="tile-open"
      @click="emit('open')"
      @contextmenu.prevent="!selecting && emit('more')"
    >
      <EntryImage v-if="complete() && (file.meta.kind === 'image' || file.meta.blobs.art)" :entry="file" :use="file.meta.kind === 'image' ? 'thumb' : 'art'">
        <span class="mono">{{ file.meta.kind === 'image' ? 'IMG' : 'VID' }}</span>
      </EntryImage>
      <span v-else class="mono">{{ complete() ? 'VID' : 'incomplete' }}</span>
    </button>
    <span v-if="file.meta.kind === 'video'" class="pointer-events-none absolute right-1 bottom-1 flex items-center gap-1 bg-bg-primary/80 px-1 text-text-primary">
      <Icon name="play" />
      <span v-if="file.meta.tags?.duration" class="mono">{{ formatDuration(file.meta.tags.duration) }}</span>
    </span>
    <span v-if="selecting" class="pointer-events-none absolute top-1.5 left-1.5 flex h-6 w-6 items-center justify-center border border-ink" :class="selected ? 'bg-ink text-paper' : 'bg-bg-primary/80'">
      <Icon v-if="selected" name="check" />
    </span>
    <button
      v-else
      class="absolute top-1 right-1 flex h-8 w-8 items-center justify-center bg-bg-primary/80 text-text-primary opacity-0 group-hover:opacity-100 focus:opacity-100 max-md:hidden"
      :aria-label="`More for ${entryTitle(file.meta)}`"
      data-testid="row-more"
      @click="emit('more')"
    >
      <Icon name="more" />
    </button>
  </div>
</template>
